import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api, { downloadFile } from '../../utils/api';
import { Archive, Download, Folder, File, ChevronDown, ChevronUp } from 'lucide-react';
import BackButton from '../../components/BackButton';

// Year color coding - distinct, accessible colors
const YEAR_COLORS = {
  2026: { bg: 'bg-blue-600', border: 'border-blue-500', text: 'text-blue-100', badge: 'bg-blue-500' },
  2025: { bg: 'bg-emerald-600', border: 'border-emerald-500', text: 'text-emerald-100', badge: 'bg-emerald-500' },
  2024: { bg: 'bg-purple-600', border: 'border-purple-500', text: 'text-purple-100', badge: 'bg-purple-500' },
  2023: { bg: 'bg-orange-600', border: 'border-orange-500', text: 'text-orange-100', badge: 'bg-orange-500' },
  2022: { bg: 'bg-rose-600', border: 'border-rose-500', text: 'text-rose-100', badge: 'bg-rose-500' },
  2021: { bg: 'bg-cyan-600', border: 'border-cyan-500', text: 'text-cyan-100', badge: 'bg-cyan-500' },
  2020: { bg: 'bg-amber-600', border: 'border-amber-500', text: 'text-amber-100', badge: 'bg-amber-500' },
  default: { bg: 'bg-slate-600', border: 'border-slate-500', text: 'text-slate-100', badge: 'bg-slate-500' }
};

const getYearColor = (dateStr) => {
  const year = new Date(dateStr).getFullYear();
  return YEAR_COLORS[year] || YEAR_COLORS.default;
};

const getYearFromDate = (dateStr) => {
  return new Date(dateStr).getFullYear();
};

const Meetings = () => {
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedArchive, setExpandedArchive] = useState(null);

  useEffect(() => {
    loadArchives();
  }, []);

  const loadArchives = async () => {
    try {
      const response = await api.get('/meetings');
      setArchives(response.data);
    } catch (error) {
      toast.error('Failed to load meeting archives');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileUrl, fileName) => {
    try {
      await downloadFile(fileUrl, fileName);
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleDownloadAll = async (archiveId, archiveTitle) => {
    try {
      toast.info('Preparing download...');
      
      // Use authenticated API call with blob response
      const response = await api.get(`/meetings/${archiveId}/download-all`, {
        responseType: 'blob',
        timeout: 120000, // 2 minute timeout for large ZIPs
      });
      
      // Check if we got an error response
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Download failed');
      }
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/zip' });
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${archiveTitle.replace(/\s+/g, '_')}.zip`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
      
      toast.success('Download started');
    } catch (error) {
      console.error('Download All error:', error);
      toast.error(error.message || 'Failed to download folder');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="member-meetings-page">
      <BackButton to="/member" />
      
      <div className="flex items-center space-x-3">
        <div className="bg-amber-100 p-3 rounded-xl">
          <Archive className="w-6 h-6 text-amber-600" />
        </div>
        <div>
          <h1 className="text-2xl font-serif font-bold text-slate-900">Meeting Archive</h1>
          <p className="text-sm text-slate-500">View and download past meeting documents</p>
        </div>
      </div>

      {/* Year Color Legend */}
      {archives.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center p-3 bg-slate-50 rounded-lg">
          <span className="text-xs text-slate-500 mr-2">Color by Year:</span>
          {Object.entries(YEAR_COLORS).filter(([key]) => key !== 'default').slice(0, 5).map(([year, colors]) => (
            <span key={year} className={`${colors.badge} text-white text-xs px-2 py-1 rounded-full`}>
              {year}
            </span>
          ))}
        </div>
      )}

      {/* Archives List */}
      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : archives.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl">
          <Folder className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">No meeting archives available</p>
          <p className="text-sm text-slate-400">Check back later for updates</p>
        </div>
      ) : (
        <div className="space-y-4">
          {archives.map((archive) => (
            <div key={archive.id} className={`bg-white border-2 ${getYearColor(archive.created_at).border} rounded-xl overflow-hidden shadow-sm`}>
              {/* Archive Header with Year Color Coding */}
              <div 
                className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors border-l-4 ${getYearColor(archive.created_at).border}`}
                onClick={() => setExpandedArchive(expandedArchive === archive.id ? null : archive.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`${getYearColor(archive.created_at).bg} p-2 rounded-lg`}>
                      <Folder className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <h3 className="font-semibold text-slate-900">{archive.title}</h3>
                        <span className={`${getYearColor(archive.created_at).badge} text-white text-xs px-2 py-0.5 rounded-full font-medium`}>
                          {getYearFromDate(archive.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500">Created: {formatDate(archive.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-slate-500 bg-slate-100 px-2 py-1 rounded">
                      {archive.documents?.length || 0} files
                    </span>
                    {expandedArchive === archive.id ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>
                {archive.description && (
                  <p className="mt-2 text-sm text-slate-600">{archive.description}</p>
                )}
              </div>
              
              {/* Expanded Content - Documents */}
              {expandedArchive === archive.id && (
                <div className="border-t border-slate-200 p-4 bg-slate-50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-slate-700">Documents</h4>
                    {archive.documents?.length > 0 && (
                      <button
                        onClick={() => handleDownloadAll(archive.id, archive.title)}
                        className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-green-600 text-white hover:bg-green-700 rounded-lg transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        <span>Download All</span>
                      </button>
                    )}
                  </div>
                  
                  {archive.documents?.length > 0 ? (
                    <div className="space-y-2">
                      {archive.documents.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200">
                          <div className="flex items-center space-x-3">
                            <File className="w-5 h-5 text-slate-400" />
                            <div>
                              <p className="text-sm font-medium text-slate-700">{doc.file_name}</p>
                              <p className="text-xs text-slate-500">
                                {formatFileSize(doc.file_size)} • Uploaded {formatDate(doc.upload_date)}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(doc.file_url, doc.file_name);
                            }}
                            className="flex items-center space-x-1 px-3 py-1.5 text-sm text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                            <span>Download</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">No documents available in this archive</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Meetings;
